import React, { useEffect, useMemo, useState } from 'react';
import * as _ from 'lodash';
import moment from 'moment';
import PropTypes from 'prop-types';
import { Row, Col, Modal } from 'react-bootstrap';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import { MdClose } from 'react-icons/md';
import Button from 'components/inputs/Button';
import TextInput from 'components/inputs/Input';
import Select from 'components/inputs/Select';
import ToggleSwitch from 'components/inputs/ToggleSwitch';
import { FETCH_CPO, GET_CPO, UPDATE_CPO } from 'actions/partnerManagement';

/**
 * CDB-1597 — settlement configuration form.
 *
 * The acceptance criteria ask for one form covering CPO Partner, Bank Details and
 * Settlement Frequency (T+X), so the partner is chosen here from a dropdown
 * rather than the settings being edited inside each partner's own screen.
 *
 * Selecting a partner loads their existing configuration, so this is both the
 * "add" and the "edit" form — a partner cannot be configured twice.
 */

/**
 * Payout rails offered for configuration.
 *
 * Both are kept available rather than replacing one with the other. The rail is
 * chosen per partner, so switching a partner between them is this dropdown and
 * nothing else — no credential change and no redeploy — and each settlement
 * records the provider that actually sent it, so history stays readable after a
 * switch. Keeping JusPay listed is what makes a rollback immediate.
 *
 * ⚠️ ORDER IS SIGNIFICANT: a new configuration defaults to the first entry. JusPay
 * leads while it is the rail with working production credentials. Putting HSBC
 * first before it is live would silently default new partners onto a provider
 * that cannot pay — they would look configured and simply never settle, which is
 * the same failure mode as the earlier unset-provider defect. Move HSBC to the
 * top once its credentials are in place; that one line is the whole change.
 *
 * The API also accepts `mock`, which is deliberately not offered here: its adapter
 * refuses to run in production, so a partner left on it would be configured,
 * appear healthy, and silently never be paid.
 */
const PROVIDER_OPTIONS = [
  { label: 'JusPay', value: 'juspay' },
  { label: 'HSBC', value: 'hsbc' },
];

/**
 * Partner roles this screen can settle.
 *
 * `role` records which way money moves: EMSP means the partner pays us, so it has
 * no place in outbound settlement. CPO and Both are the ones we pay.
 */
const SETTLEABLE_ROLES = ['CPO', 'Both'];

const validationSchema = Yup.object().shape({
  ocpiCredential: Yup.string().required('Select a CPO partner'),
  // Required, not defaulted server-side: the payout path resolves the rail from
  // this field and refuses to pay rather than guessing one. Left unset, a partner
  // configures cleanly and settles into batches, then fails at the payment step.
  provider: Yup.string().required('Select a payout provider'),
  settlement_frequency_days: Yup.number()
    .typeError('Enter a whole number of days')
    .integer('Enter a whole number of days')
    .min(0, 'Must be between 0 and 30')
    .max(30, 'Must be between 0 and 30')
    .required('Required'),
  // Required rather than defaulted: this is the boundary against the finance
  // team's manual settlement. Guessing it either double-pays a day or skips one.
  settlement_start_date: Yup.mixed().required('Required'),
  max_cap: Yup.number().typeError('Enter a valid amount').min(0, 'Cannot be negative').required('Required'),
  min_settlement_amount: Yup.number().typeError('Enter a valid amount').min(0, 'Cannot be negative'),
  owner: Yup.string().required('Required'),
  account_no: Yup.string()
    .trim()
    .matches(/^[0-9]{6,20}$/, 'Enter a valid account number')
    .required('Required'),
  // Standard IFSC shape: 4 letters, 0, then 6 alphanumerics. Validated here as
  // well as server-side, because a wrong IFSC only fails at payout time.
  ifscCode: Yup.string()
    .trim()
    .matches(/^[A-Z]{4}0[A-Z0-9]{6}$/, 'Enter a valid IFSC code')
    .required('Required'),
  bankName: Yup.string().required('Required'),
  branch: Yup.string().required('Required'),
});

const CpoSettlementConfigModal = ({ show, onClose, onSaved, partnerId, onExited }) => {
  const dispatch = useDispatch();

  const partnerList = useSelector((state) => state.partnerManagement.partnerDetails);
  const selectedPartner = useSelector((state) => state.partnerManagement.partnerInfo);

  /**
   * Which partner this form is actually about — the one passed in when editing, or
   * the one chosen from the dropdown when adding.
   *
   * Tracked here because `partnerInfo` is shared store state that survives the
   * modal closing. Opening "Configure Partner" after editing a card therefore
   * found the previously edited partner still sitting in the store and pre-filled
   * every field with it, including the dropdown (CDB-1744).
   */
  const [pickedId, setPickedId] = useState('');

  useEffect(() => {
    if (show) {
      // A high limit rather than pagination: this is a dropdown, and a partly
      // loaded partner list would silently hide partners that need configuring.
      dispatch({ type: FETCH_CPO.REQUEST, payload: { limit: 999 } });
      // Opened from a configured partner's Edit action: load that partner so the
      // form shows what is currently set rather than starting from defaults.
      if (partnerId) {
        dispatch({ type: GET_CPO.REQUEST, payload: partnerId });
      } else {
        // Adding: nothing is chosen yet, so the form must start empty regardless
        // of whose details the store happens to be holding.
        setPickedId('');
      }
    }
  }, [show, dispatch, partnerId]);

  // Editing an existing partner must not allow the dropdown to be changed —
  // switching partners mid-edit would apply one partner's bank details to another.
  const isEditing = !!partnerId;

  /**
   * The dropdown's options are held locally rather than read straight from the
   * store.
   *
   * Selecting a partner dispatches GET_CPO to load their existing configuration,
   * and that request's reducer clears `partnerDetails` — which emptied the very
   * dropdown that was just used, so reopening it showed "no options". Keeping the
   * last non-empty list here makes the options immune to that, without changing
   * shared reducer behaviour other screens rely on.
   */
  const [loadedPartners, setLoadedPartners] = useState([]);

  useEffect(() => {
    if (_.isArray(partnerList) && partnerList.length) {
      setLoadedPartners(partnerList);
    }
  }, [partnerList]);

  const partnerOptions = useMemo(
    () =>
      loadedPartners
        // Only roles we pay. `role` records the direction of the relationship:
        // EMSP is inbound — the partner pays us — while CPO and Both are outbound,
        // which is what this screen settles. Listing an EMSP credential invited a
        // configuration that can never legitimately be paid, and six had already
        // been created that way on UAT.
        //
        // Soft-deleted credentials are already excluded server-side: the model uses
        // mongoose-delete with overrideMethods 'all', so find() omits them unless
        // asked (verified: 221 documents in the collection, 67 returned).
        .filter((partner) => SETTLEABLE_ROLES.includes(partner.role))
        .map((partner) => {
          // Verified against both environments: `name` is never populated (0/132 in
          // production, 0/219 in UAT) and `party_id` does not exist. The real
          // fields are partner_name then partyId, so the chain leads with those.
          const name = partner.partner_name || partner.name || partner.partyId || _.get(partner, 'roles[0].business_details.name') || partner.id;
          // A partner can hold more than one OCPI credential, and duplicates carry
          // the same name AND the same party id — UAT has two "Evnnovator (EVX)"
          // records. Configuring the wrong one is silent: settlement is set up
          // against a credential the sessions never land on, so nothing ever
          // settles. The id tail is what actually separates them.
          const suffix = [partner.partyId, partner.id ? String(partner.id).slice(-6) : null].filter(Boolean).join(' · ');
          return { value: partner.id, label: suffix ? `${name} (${suffix})` : name };
        }),
    [loadedPartners]
  );

  /**
   * The partner whose details this form may show.
   *
   * `partnerInfo` is only trusted when it is the partner in play: editing means the
   * id passed in, adding means the id just chosen. Anything else is a leftover from
   * a previous open, and reading it is what pre-filled a blank form.
   */
  const activeId = partnerId || pickedId;
  const loadedPartner = activeId && _.get(selectedPartner, 'id') === activeId ? selectedPartner : null;

  const config = _.get(loadedPartner, 'cpo_settlement_config') || {};

  /**
   * A provider already set outside this form — `mock` for a test partner — is added
   * to the options so editing that partner shows its actual rail and preserves it,
   * rather than displaying a blank select over a value the form would then re-save.
   */
  const providerOptions = useMemo(() => {
    if (config.provider && !PROVIDER_OPTIONS.some((option) => option.value === config.provider)) {
      return [...PROVIDER_OPTIONS, { label: _.startCase(config.provider), value: config.provider }];
    }
    return PROVIDER_OPTIONS;
  }, [config.provider]);

  // A native date input works in YYYY-MM-DD strings, for both its value and `min`.
  // Today is the floor for a new configuration; a partner already configured with an
  // earlier date keeps that date as the floor, so the input never rejects the value
  // it is currently displaying.
  const DATE_FMT = 'YYYY-MM-DD';
  const existingStart = config.settlement_start_date ? moment(config.settlement_start_date).startOf('day') : null;
  const today = moment().startOf('day');
  const minStartDate = (existingStart && existingStart.isBefore(today) ? existingStart : today).format(DATE_FMT);

  const initialValues = {
    ocpiCredential: activeId || '',
    enabled: config.enabled ?? true,
    settlement_frequency_days: config.settlement_frequency_days ?? 1,
    settlement_start_date: config.settlement_start_date ? moment(config.settlement_start_date).format(DATE_FMT) : '',
    max_cap: config.max_cap ?? 10000,
    min_settlement_amount: config.min_settlement_amount ?? 0,
    // An absent run_mode means verify-only. Never inferred as live, because live
    // is the state in which money leaves the account.
    settlement_live: (config.run_mode ?? 'compute_only') === 'live',
    // Defaulted in the form, not on the server: the choice stays visible and
    // recorded against the partner, while a save can no longer produce a partner
    // that settles correctly but cannot be paid.
    provider: config.provider || PROVIDER_OPTIONS[0].value,
    owner: _.get(loadedPartner, 'owner', '') || '',
    account_no: _.get(loadedPartner, 'account_no', '') || '',
    ifscCode: _.get(loadedPartner, 'ifscCode', '') || '',
    bankName: _.get(loadedPartner, 'bankName', '') || '',
    branch: _.get(loadedPartner, 'branch', '') || '',
  };

  return (
    <Modal show={show} size="lg" centered onHide={onClose} onExited={onExited}>
      <div className="settlement-config-modal">
        <div className="settlement-config-modal__header">
          <div className="settlement-config-modal__title">
            {isEditing ? 'Edit Partner Settlement Configuration' : 'CPO Partner Settlement Configuration'}
          </div>
          <MdClose size={28} onClick={onClose} className="cursor-pointer" />
        </div>

        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={(data) => {
            dispatch({
              type: UPDATE_CPO.REQUEST,
              // `updateCpo` takes a FLAT object and reads `id` off it for the URL,
              // then sends the rest as the body. Wrapping it in { data, roleId }
              // produced PATCH /ocpi_credential/undefined and a 400 rejecting
              // `data` and `roleId` as unknown fields.
              payload: {
                id: data.ocpiCredential,
                bankName: data.bankName,
                branch: data.branch,
                account_no: data.account_no,
                ifscCode: data.ifscCode,
                owner: data.owner,
                cpo_settlement_config: {
                  enabled: !!data.enabled,
                  run_mode: data.settlement_live ? 'live' : 'compute_only',
                  settlement_frequency_days: Number(data.settlement_frequency_days),
                  settlement_start_date: data.settlement_start_date ? moment(data.settlement_start_date).toISOString() : null,
                  max_cap: Number(data.max_cap),
                  min_settlement_amount: Number(data.min_settlement_amount),
                  provider: data.provider,
                },
              },
              cb: () => {
                onSaved && onSaved();
                onClose();
              },
            });
          }}
        >
          {({ handleSubmit, values, errors, touched, setFieldValue, handleChange, setFieldTouched }) => (
            <Form onSubmit={handleSubmit}>
              <div className="settlement-config-modal__body">
                <Row>
                  <Col lg={12}>
                    <Select
                      isRequired
                      label="CPO Partner"
                      placeholder="Select CPO partner"
                      options={partnerOptions}
                      value={values.ocpiCredential}
                      isDisabled={isEditing}
                      onChange={(val) => {
                        setFieldValue('ocpiCredential', val);
                        // Recorded so `loadedPartner` accepts the details that are
                        // about to arrive for this partner, and only those.
                        setPickedId(val);
                        // Load whatever is already configured for this partner, so
                        // the form edits the existing setup instead of silently
                        // overwriting it with defaults.
                        if (val) {
                          dispatch({ type: GET_CPO.REQUEST, payload: val });
                        }
                      }}
                      error={errors.ocpiCredential && touched.ocpiCredential ? errors.ocpiCredential : null}
                    />
                  </Col>
                </Row>

                <div className="settlement-config-modal__section">Settlement Frequency</div>
                <Row>
                  <Col lg={6}>
                    <TextInput
                      isRequired
                      type="number"
                      label="Settlement Frequency (T+X, days)"
                      placeholder="1 = settle yesterday's sessions today"
                      name="settlement_frequency_days"
                      min={0}
                      max={30}
                      value={values.settlement_frequency_days}
                      onChange={handleChange}
                      // A number input changes value on scroll once focused, so
                      // scrolling the form silently altered the settlement cycle.
                      // Blurring on wheel stops the field capturing the scroll.
                      onWheel={(e) => e.target.blur()}
                      onBlur={() => setFieldTouched('settlement_frequency_days', true)}
                      error={errors.settlement_frequency_days && touched.settlement_frequency_days ? errors.settlement_frequency_days : null}
                    />
                  </Col>
                  <Col lg={6}>
                    {/* Native date input, the same as the What's New slide dates.
                        The browser's own picker greys out anything before `min`, so
                        the restriction is visible rather than the click silently
                        doing nothing — and it needs no extra library. */}
                    <TextInput
                      isRequired
                      type="date"
                      label="Settlement Start Date"
                      name="settlement_start_date"
                      // Past dates are blocked because this boundary decides what the
                      // first run reaches back to: a backdated start would sweep
                      // months of history the finance team has already settled by
                      // hand into one payment. A partner already configured with an
                      // earlier date keeps it as the floor, so editing another field
                      // cannot force the date to move.
                      min={minStartDate}
                      value={values.settlement_start_date}
                      onChange={handleChange}
                      onBlur={() => setFieldTouched('settlement_start_date', true)}
                      error={errors.settlement_start_date && touched.settlement_start_date ? errors.settlement_start_date : null}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col lg={12}>
                    <small className="settlement-config-modal__hint">
                      Sessions completing before the start date stay with the finance team&apos;s manual settlement and are never picked up here.
                    </small>
                  </Col>
                </Row>

                <div className="settlement-config-modal__section">Bank Details</div>
                <Row>
                  <Col lg={6}>
                    <TextInput
                      isRequired
                      label="Account Holder Name"
                      placeholder="Name exactly as on the bank account"
                      name="owner"
                      value={values.owner}
                      onChange={handleChange}
                      onBlur={() => setFieldTouched('owner', true)}
                      error={errors.owner && touched.owner ? errors.owner : null}
                    />
                  </Col>
                  <Col lg={6}>
                    <TextInput
                      isRequired
                      label="Account Number"
                      placeholder="Enter account number"
                      name="account_no"
                      value={values.account_no}
                      onChange={handleChange}
                      onBlur={() => setFieldTouched('account_no', true)}
                      error={errors.account_no && touched.account_no ? errors.account_no : null}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col lg={6}>
                    <TextInput
                      isRequired
                      label="IFSC Code"
                      placeholder="e.g. HDFC0001234"
                      name="ifscCode"
                      value={values.ifscCode}
                      // Upper-cased as typed, since IFSC codes always are and the
                      // validation pattern expects it.
                      onChange={(e) => setFieldValue('ifscCode', (e.target.value || '').toUpperCase())}
                      onBlur={() => setFieldTouched('ifscCode', true)}
                      error={errors.ifscCode && touched.ifscCode ? errors.ifscCode : null}
                    />
                  </Col>
                  <Col lg={6}>
                    <TextInput
                      isRequired
                      label="Bank Name"
                      placeholder="Enter bank name"
                      name="bankName"
                      value={values.bankName}
                      onChange={handleChange}
                      onBlur={() => setFieldTouched('bankName', true)}
                      error={errors.bankName && touched.bankName ? errors.bankName : null}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col lg={6}>
                    <TextInput
                      isRequired
                      label="Branch"
                      placeholder="Enter branch"
                      name="branch"
                      value={values.branch}
                      onChange={handleChange}
                      onBlur={() => setFieldTouched('branch', true)}
                      error={errors.branch && touched.branch ? errors.branch : null}
                    />
                  </Col>
                </Row>

                <div className="settlement-config-modal__section">Limits &amp; Controls</div>
                <Row>
                  <Col lg={6}>
                    <TextInput
                      isRequired
                      type="number"
                      label="Approval Required Above (INR)"
                      placeholder="Enter amount"
                      name="max_cap"
                      min={0}
                      value={values.max_cap}
                      onChange={handleChange}
                      onWheel={(e) => e.target.blur()}
                      onBlur={() => setFieldTouched('max_cap', true)}
                      error={errors.max_cap && touched.max_cap ? errors.max_cap : null}
                    />
                  </Col>
                  <Col lg={6}>
                    <TextInput
                      type="number"
                      label="Minimum Settlement Amount (INR)"
                      placeholder="Enter amount"
                      name="min_settlement_amount"
                      min={0}
                      value={values.min_settlement_amount}
                      onChange={handleChange}
                      onWheel={(e) => e.target.blur()}
                      onBlur={() => setFieldTouched('min_settlement_amount', true)}
                      error={errors.min_settlement_amount && touched.min_settlement_amount ? errors.min_settlement_amount : null}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col lg={6}>
                    <Select
                      isRequired
                      label="Payout Provider"
                      placeholder="Select payout provider"
                      options={providerOptions}
                      value={values.provider}
                      onChange={(val) => setFieldValue('provider', val)}
                      error={errors.provider && touched.provider ? errors.provider : null}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col lg={6}>
                    <div className="switch-container">
                      <label htmlFor="enabled">Settlement Enabled</label>
                      <ToggleSwitch name="enabled" checked={values.enabled || false} onChange={(e) => setFieldValue('enabled', e.target.checked)} />
                    </div>
                  </Col>
                  {/* "Automatic Payments" rather than "Live Payments": with it off,
                      settlements are still real and still payable — by hand from the
                      settlement list. What the switch governs is whether the daily
                      run pays them without anyone present. */}
                  <Col lg={6}>
                    <div className="switch-container">
                      <label htmlFor="settlement_live">Automatic Payments</label>
                      <ToggleSwitch
                        name="settlement_live"
                        checked={values.settlement_live || false}
                        onChange={(e) => setFieldValue('settlement_live', e.target.checked)}
                      />
                    </div>
                  </Col>
                </Row>
                <Row>
                  <Col lg={12}>
                    {/* Stated on the form rather than left to documentation,
                        because this switch is what allows money to move. */}
                    <small className={`settlement-config-modal__hint${values.settlement_live ? ' settlement-config-modal__hint--live' : ''}`}>
                      {values.settlement_live
                        ? `ON — the daily run sends any settlement of ₹${Number(values.max_cap) || 0} or less to this partner's bank account automatically. Anything above ₹${
                            Number(values.max_cap) || 0
                          } waits for someone to approve it.`
                        : 'OFF — nothing is paid automatically. Settlements are still created with real figures and can be paid by hand from the settlement list.'}
                    </small>
                  </Col>
                </Row>
              </div>

              <div className="settlement-config-modal__actions">
                <Button className="settlement-action-btn" onClick={onClose}>
                  Cancel
                </Button>
                <Button type="submit" className="settlement-action-btn">
                  {isEditing ? 'Update Configuration' : 'Save Configuration'}
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </Modal>
  );
};

CpoSettlementConfigModal.propTypes = {
  show: PropTypes.bool,
  onClose: PropTypes.func,
  onSaved: PropTypes.func,
  partnerId: PropTypes.string,
  onExited: PropTypes.func,
};

export default CpoSettlementConfigModal;
